import { z } from 'zod';

import { contentSchema } from './content';
import { defaultNullish, mongoIdSchema } from './shared';

export enum ContentAiFunnelStatus {
  // we use this step if the user want to save extra information on the funnel without starting to generate titles/outlines/content
  Brief = 'brief',

  ReqGenTitles = 'reqGenTitles',
  ReqGenTitlesLoad = 'reqGenTitlesLoad',
  ReqGenTitlesError = 'reqGenTitlesError',

  ReqGenOutlines = 'reqGenOutlines',
  ReqGenOutlinesLoad = 'reqGenOutlinesLoad',
  ReqGenOutlinesError = 'reqGenOutlinesError',

  ReqGenContent = 'reqGenContent',
  ReqGenContentLoad = 'reqGenContentLoad',
  ReqGenContentError = 'reqGenContentError',

  Done = 'done',
}

export enum ContentType {
  BlogPost = 'blog_post',
  Listicle = 'listicle',
  VSPage = 'vs_page',
  Review = 'review',
  News = 'news',
  WebPage = 'webpage',
}

const contentOutlineSchema = z.array(
  z.object({
    text: z.string(),
    tag: z.string(),
    instructions: z.array(z.string()),
  })
);
export type ContentOutline = z.infer<typeof contentOutlineSchema>;

export const contentFunnelSchema = z.object({
  _id: mongoIdSchema,
  createdAt: z.number(),
  updatedAt: z.number(),

  contentId: contentSchema.shape._id,
  selectedTitle: z.string().trim(),
  selectedOutline: contentOutlineSchema,
  selectedVoice: z.object({
    brandId: z.string().nullish(),
    contentDirection: z.string().max(5000),
    outlineStructure: z.string().max(5000),
    hasFaq: z.boolean().default(false),
    hasKeyTakeaways: z.boolean().default(false),
  }),
  selectedMetaTitle: z.string().trim(),
  customizedAiTitles: defaultNullish(z.array(z.string())).default([]),
  contentType: z.nativeEnum(ContentType).nullish(),
  talkAboutBrandInIntro: z.boolean().nullish(),
  talkAboutBrandInBody: z.boolean().nullish(),
  talkAboutBrandInConclusion: z.boolean().nullish(),
  supportingMaterials: z.array(z.object({ url: z.string(), text: z.string() })).nullish(),
  customWordCount: z.boolean().nullish(),
  customWordCountValue: z.number().nullish(),

  // this data comes from the last step in the funnel, the generation
  generatedMetaDescription: z.string(),
  generatedAlertSize: z.boolean(),
  generatedContent: z.string(),

  aiTitles: z.array(z.string()).nullish(),
  aiOutlines: contentOutlineSchema,

  status: z.enum([
    ContentAiFunnelStatus.Brief,
    ContentAiFunnelStatus.ReqGenTitles,
    ContentAiFunnelStatus.ReqGenTitlesLoad,
    ContentAiFunnelStatus.ReqGenTitlesError,

    ContentAiFunnelStatus.ReqGenOutlines,
    ContentAiFunnelStatus.ReqGenOutlinesLoad,
    ContentAiFunnelStatus.ReqGenOutlinesError,

    ContentAiFunnelStatus.ReqGenContent,
    ContentAiFunnelStatus.ReqGenContentLoad,
    ContentAiFunnelStatus.ReqGenContentError,

    ContentAiFunnelStatus.Done,
  ]),
});
export type ContentFunnel = z.infer<typeof contentFunnelSchema>;
